import { Box } from '@mui/material';
import { Timeline } from '@xzdarcy/react-timeline-editor';
import '@xzdarcy/react-timeline-editor/dist/react-timeline-editor.css';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';

// ── Pure utilities (self-contained copies so this component has no parent deps) ──

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const hexToRgb = (hex) => {
    const clean = String(hex || '').replace('#', '').trim();
    if (![3, 6].includes(clean.length)) return null;
    const normalized =
        clean.length === 3
            ? clean
                  .split('')
                  .map((c) => `${c}${c}`)
                  .join('')
            : clean;
    const int = Number.parseInt(normalized, 16);
    if (!Number.isFinite(int)) return null;
    return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
};

const resolveSubtitleBackground = (style) => {
    const color = style?.backgroundColor || '#000000';
    const opacityPercent = Number(style?.backgroundOpacity);
    const alpha = Number.isFinite(opacityPercent) ? clamp(opacityPercent, 0, 100) / 100 : 0;
    if (typeof color === 'string' && color.startsWith('rgba(')) return color;
    if (typeof color === 'string' && color.startsWith('rgb('))
        return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
    const rgb = hexToRgb(color);
    if (!rgb) return `rgba(0, 0, 0, ${alpha})`;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

// Parse an rgba()/rgb() or hex color string into components
const parseColor = (input) => {
    if (!input) return null;
    if (typeof input !== 'string') return null;
    const s = input.trim();
    const rgba = s.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\)/i);
    if (rgba) {
        return { r: Number(rgba[1]), g: Number(rgba[2]), b: Number(rgba[3]), a: rgba[4] ? Number(rgba[4]) : 1 };
    }
    if (s.startsWith('#')) {
        const rgb = hexToRgb(s);
        if (rgb) return { r: rgb.r, g: rgb.g, b: rgb.b, a: 1 };
    }
    return null;
};

// Choose a readable foreground color (black or white) against the given background
const readableTextColorForBg = (bg) => {
    if (!bg) return '#ffffff';
    // Perceived brightness
    const Y = 0.299 * bg.r + 0.587 * bg.g + 0.114 * bg.b;
    return Y < 140 ? '#ffffff' : '#000000';
};

const buildActionLabel = (action) => {
    const text = action?.text || '';
    const preview = text.replace(/\s+/g, ' ').trim();
    if (!preview) return 'Empty subtitle';
    if (preview.length < 48) return preview;
    return `${preview.slice(0, 45)}...`;
};

// ── Default effects object (can be overridden via props) ──

const DEFAULT_EFFECTS = {
    subtitle: { id: 'subtitle', name: 'Subtitle' },
    audio:    { id: 'audio',    name: 'Audio' },
};

// ── Container styles ──

const containerSx = {
    width: '100%',
    overflowX: 'auto',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    p: 0.25,
    bgcolor: '#0b1220',
    backgroundImage:
        'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0)), repeating-linear-gradient(90deg, rgba(255,255,255,0.06) 0, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 24px)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
    '& .timeline-editor': {
        width: '100% !important',
        height: '100px !important',
        minHeight: '100px !important',
        backgroundColor: 'transparent',
        pointerEvents: 'auto !important',
    },
    '& .timeline-editor-time-area': { height: '24px !important' },
    '& .timeline-editor-edit-area': { marginTop: '4px !important' },
    '& .timeline-editor-cursor': {
        top: '24px !important',
        height: 'calc(100% - 24px) !important',
    },
    '& .timeline-editor-action .timeline-editor-action-left-stretch':  { width: '8px', zIndex: 10 },
    '& .timeline-editor-action .timeline-editor-action-right-stretch': { width: '8px', zIndex: 10 },
    '& .timeline-editor-action .timeline-editor-action-left-stretch:after, & .timeline-editor-action .timeline-editor-action-right-stretch:after': {
        borderTopWidth: '11px',
        borderBottomWidth: '11px',
    },
    '& .timeline-editor-time-unit':       { height: '3px !important' },
    '& .timeline-editor-time-unit-big':   { height: '6px !important' },
    '& .timeline-editor-time-unit-scale': { fontSize: '10px' },
};

/**
 * SubtitleTimeline — reusable timeline editor for subtitle + audio tracks.
 *
 * Props:
 *   rows                  {Array}    Timeline rows (editorData), including any audio row.
 *   selectedActionId      {string}   ID of the currently selected subtitle action.
 *   selectedAudioSegmentId{string}   ID of the currently selected audio segment.
 *   defaultStyle          {object}   Default subtitle style (used for chip rendering).
 *   effects               {object}   Effects map override (defaults to subtitle + audio).
 *   onSubtitleClick       {fn(actionId, time)}  Called when a subtitle chip is clicked.
 *   onAudioSegmentClick   {fn(segmentId)}        Called when an audio chip is clicked.
 *   onTimeChange          {fn(time)}  Called on cursor drag and timeline area click.
 *   onChange              {fn(rows)}  Called when rows are rearranged / resized.
 *
 * The ref is forwarded to the underlying Timeline instance so callers can invoke
 * timelineRef.current.setTime(t) to sync the playhead.
 */
const SubtitleTimeline = forwardRef(function SubtitleTimeline(
    {
        rows = [],
        selectedActionId,
        selectedAudioSegmentId,
        currentTime = 0,
        defaultStyle = {},
        effects = DEFAULT_EFFECTS,
        onSubtitleClick,
        onAudioSegmentClick,
        onTimeChange,
        onChange,
    },
    ref,
) {
    // Store currentTime in a ref so onClickAction always reads the latest value
    // WITHOUT making currentTime a render dependency. This is critical: the
    // @xzdarcy/react-timeline-editor library resets any in-progress drag whenever
    // the component re-renders, so we must not re-render on every video tick.
    const currentTimeRef = useRef(currentTime);
    currentTimeRef.current = currentTime;

    const innerTimelineRef = useRef(null);
    const containerRef = useRef(null);

    // Expose setTime (delegated to the library ref) and scrollToTime to parent.
    useImperativeHandle(ref, () => ({
        setTime: (t) => innerTimelineRef.current?.setTime(t),
        scrollToTime: (time) => {
            // The library manages horizontal scroll internally via React state,
            // not CSS overflow. Use the exposed setScrollLeft API.
            const editArea = containerRef.current?.querySelector('.timeline-editor-edit-area');
            const visibleWidth = editArea?.clientWidth || containerRef.current?.clientWidth || 0;
            if (!visibleWidth) return;

            // These must match the scale/scaleWidth/startLeft props below.
            const timeScale = 64; // scaleWidth(64) / scale(1) = 64 px per second
            const startLeft = 4;

            const targetPx = startLeft + time * timeScale;
            // Position the subtitle at ~30 % from the left (well within the 20–80 % band).
            const desired = Math.max(0, targetPx - visibleWidth * 0.3);
            innerTimelineRef.current?.setScrollLeft?.(desired);
        },
    }), []);

    return (
        <Box ref={containerRef} sx={containerSx}>
            <Box sx={{ minWidth: '100%' }}>
                <Timeline
                    ref={innerTimelineRef}
                    editorData={rows}
                    effects={effects}
                    scale={1}
                    scaleSplitCount={8}
                    scaleWidth={64}
                    startLeft={4}
                    minScaleCount={12}
                    maxScaleCount={500}
                    rowHeight={22}
                    gridSnap
                    dragLine
                    autoReRender
                    style={{ width: '100%' }}
                    getActionRender={(action) => {
                        if (action.effectId === 'audio') {
                            const isSelected = action.id === selectedAudioSegmentId;
                            return (
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        bgcolor: isSelected ? '#0d9488' : '#134e4a',
                                        border: isSelected ? '2px solid #2dd4bf' : '1px solid #0d9488',
                                        color: '#ccfbf1',
                                        position: 'relative',
                                        zIndex: 3,
                                        pointerEvents: 'none',
                                        textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        px: 0.7,
                                        overflow: 'hidden',
                                        fontSize: '9px',
                                        borderRadius: '4px',
                                        fontWeight: 600,
                                    }}
                                >
                                    <span style={{ display: 'block', width: '100%', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                        🎵 {action.label || 'Audio'}
                                    </span>
                                </Box>
                            );
                        }

                        // Merge defaults and clamp background opacity for better legibility
                        const raw = { ...defaultStyle, ...(action.style || {}) };
                        const bgOpacityPercent = Number(raw.backgroundOpacity ?? defaultStyle.backgroundOpacity ?? 0);
                        const bgOpacityClamped = clamp(bgOpacityPercent, 6, 40); // keep backgrounds relatively translucent
                        const style = { ...raw, backgroundOpacity: bgOpacityClamped };
                        const bgCss = resolveSubtitleBackground(style);
                        const bgParsed = parseColor(bgCss) || { r: 0, g: 0, b: 0, a: 0.5 };

                        // Determine best text color: prefer user color if it contrasts, otherwise auto-pick
                        let textColor = '#ffffff';
                        if (raw.color) {
                            const textParsed = parseColor(raw.color);
                            if (textParsed) {
                                const brightnessDiff = Math.abs((0.299 * bgParsed.r + 0.587 * bgParsed.g + 0.114 * bgParsed.b) - (0.299 * textParsed.r + 0.587 * textParsed.g + 0.114 * textParsed.b));
                                textColor = brightnessDiff > 60 ? raw.color : readableTextColorForBg(bgParsed);
                            } else {
                                textColor = readableTextColorForBg(bgParsed);
                            }
                        } else {
                            textColor = readableTextColorForBg(bgParsed);
                        }

                        const isSelected = action.id === selectedActionId;
                        return (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    bgcolor: bgCss,
                                    border: isSelected ? '2px solid #2563eb' : '1px solid rgba(0,0,0,0.25)',
                                    color: textColor,
                                    position: 'relative',
                                    zIndex: 3,
                                    pointerEvents: 'none',
                                    textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 0.7,
                                    overflow: 'hidden',
                                    fontWeight: style.bold ? 700 : 500,
                                    fontStyle: style.italic ? 'italic' : 'normal',
                                    textDecoration: style.underline ? 'underline' : 'none',
                                    textAlign: style.align,
                                    fontSize: `${Math.max(9, Number(style.fontSize) * 0.18)}px`,
                                    borderRadius: '4px',
                                    boxShadow: isSelected
                                        ? '0 0 0 1px rgba(255,255,255,0.08) inset'
                                        : '0 1px 0 rgba(255,255,255,0.05) inset',
                                }}
                            >
                                <span style={{ display: 'block', width: '100%', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                    {buildActionLabel(action)}
                                </span>
                            </Box>
                        );
                    }}
                    onClickAction={(_, param) => {
                        if (param.action.effectId === 'audio') {
                            onAudioSegmentClick?.(param.action.id);
                            // Only move the playhead if the cursor is NOT already inside the segment.
                            // If the user has already positioned the cursor at their intended split
                            // point, clicking the chip to select it must not overwrite that position.
                            const segStart = Number(param.action.start ?? 0);
                            const segEnd = Number(param.action.end ?? 0);
                            if (currentTimeRef.current < segStart || currentTimeRef.current >= segEnd) {
                                onTimeChange?.(param.time);
                            }
                            return;
                        }
                        // Use param.time (actual click position) so the cursor goes exactly where
                        // the user clicked, not to the start of the subtitle chip.
                        onSubtitleClick?.(param.action.id, Number(param.time ?? param.action.start));
                    }}
                    onCursorDrag={onTimeChange}
                    onClickTimeArea={(time) => {
                        onTimeChange?.(time);
                        return true;
                    }}
                    onChange={onChange}
                />
            </Box>
        </Box>
    );
});

// Wrap with React.memo and a custom comparator that intentionally excludes
// `currentTime`. currentTime updates on every video frame (~100ms) and would
// otherwise trigger a re-render that forces the Timeline library to reset any
// active drag operation, causing the "snaps back" behaviour. The latest
// currentTime is always available inside the component via currentTimeRef.
export default React.memo(SubtitleTimeline, (prev, next) =>
    prev.rows === next.rows &&
    prev.selectedActionId === next.selectedActionId &&
    prev.selectedAudioSegmentId === next.selectedAudioSegmentId &&
    prev.defaultStyle === next.defaultStyle &&
    prev.effects === next.effects &&
    prev.onSubtitleClick === next.onSubtitleClick &&
    prev.onAudioSegmentClick === next.onAudioSegmentClick &&
    prev.onTimeChange === next.onTimeChange &&
    prev.onChange === next.onChange,
);
